import React from 'react';
import PageLayout from '../components/PageLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Heart, Users, Globe, Zap, Shield } from 'lucide-react';
import { FlyNOutLogo } from '../components/Navbar';
import SEOHead from '../components/SEOHead';

export default function About() {
  return (
    <PageLayout>
      <SEOHead
        title="About Fly N Out — The Travel Community Story"
        description="Fly N Out was built by frequent flyers who believe every flight should be a shared experience. Learn about our mission to connect travelers worldwide."
        url="/about"
        keywords="about Fly N Out, travel community, flight social network, about us, team"
      />

      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#111827] to-[#1E3A8A]/90 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <FlyNOutLogo size={64} className="mx-auto mb-6" />
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk' }}>About Fly N Out</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            We believe every flight is more than a journey — it's a community waiting to be discovered.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Mission */}
        <Card className="fly-card p-8 mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk' }}>Our Mission</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Fly N Out was born from a simple frustration: why do millions of people board the same plane, wait at the same gate, and sit in the same terminal without ever connecting? Air travel is one of the most social environments on earth, yet it remains stubbornly solitary.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            We built Fly N Out to change that. Our platform brings the power of community to every flight, every airport, and every layover — turning strangers into travel companions and making every journey more informed, more fun, and more human.
          </p>
        </Card>

        {/* Values */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {[
            { icon: Heart, title: 'Community First', desc: 'Every feature we build puts real traveler connections at the center.' },
            { icon: Shield, title: 'Safe & Trusted', desc: 'AI moderation and community standards keep Fly N Out welcoming for all.' },
            { icon: Globe, title: 'Global & Local', desc: 'We serve travelers from every country with local language support.' },
            { icon: Zap, title: 'Real-time', desc: 'Airport updates happen in seconds. Our platform keeps pace with travel.' },
          ].map(({ icon: Icon, title, desc }) => (
            <Card key={title} className="fly-card p-5">
              <Icon className="w-6 h-6 text-[#FF4500] mb-3" />
              <h3 className="font-semibold mb-1" style={{ fontFamily: 'Space Grotesk' }}>{title}</h3>
              <p className="text-sm text-muted-foreground">{desc}</p>
            </Card>
          ))}
        </div>

        {/* Team */}
        <Card className="fly-card p-8 mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk' }}>Built by Travelers, for Travelers</h2>
          <p className="text-muted-foreground leading-relaxed">
            The Fly N Out team is a group of frequent flyers, aviation enthusiasts, and community builders based across Riyadh, Dubai, and London. We collectively log over 200 flights per year and have experienced every airport frustration imaginable — from missed connections to 8-hour layovers alone.
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3">
            That shared experience is what drives every product decision we make. We build what we wish had existed when we were sitting alone at Gate B22.
          </p>
        </Card>

        <div className="text-center">
          <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk' }}>Ready to Join?</h2>
          <div className="flex gap-3 justify-center">
            <Button className="fly-btn-primary" asChild><Link to="/">Get Started Free</Link></Button>
            <Button variant="outline" asChild><Link to="/contact">Contact Us</Link></Button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
